import { CHART_COLORS } from "@/components/charts/event-chart";
import { ChangeArrow } from "@/components/event-detail/change-arrow";
import { SmartImage } from "@/components/ui/smart-image";
import { getBrandLogoGray } from "@/lib/brand-config";
import { formatCentPriceAsChancePercent } from "@/utils/price";
import { calc, get } from "@/utils/utils";
import type { ChartRowData, GuessItem } from "../../app/event-detail/types";

interface LegendSectionProps {
    displayedGuessList: GuessItem[];
    guessList: GuessItem[];
    selectedChartRow: ChartRowData;
}

/**
 * 图例显示区域
 * - 单个竞猜：显示大号百分比 + 涨跌幅 + Logo
 * - 多个竞猜：显示颜色点 + 标题 + 百分比
 */
export function LegendSection( { displayedGuessList, guessList, selectedChartRow }: LegendSectionProps ) {
    // 过滤掉 status 为 3 的项（只有 status 不为 3 的才可展示）
    const filteredGuessList = displayedGuessList.filter( ( guess ) => {
        const status = get( guess, "status", 0 );
        return status !== 3;
    } );

    if ( filteredGuessList.length === 0 ) {
        return null;
    }

    const isSingleGuess = filteredGuessList.length === 1;

    return (
        <div className="mb-3">
            <div className="flex flex-wrap gap-x-4 gap-y-3">
                {filteredGuessList.map( ( guess ) => {
                    const originalIndex = guessList.findIndex( ( g ) =>
                        get( g, "guessNo", "" ) === get( guess, "guessNo", "" )
                    );

                    // 计算涨跌幅（仅单个时）
                    let changePercent = 0;
                    let isUp = true;

                    if ( isSingleGuess ) {
                        const currentValue = typeof get( selectedChartRow, guess.guessNo, undefined ) === "number"
                            ? get( selectedChartRow, guess.guessNo, 0 )
                            : guess.yesRealtimePrice
                            ? Number( get( guess, "yesRealtimePrice", 0 ) )
                            : 0;

                        const firstDataPoint = get( selectedChartRow, "firstDataPoint", null );
                        if ( firstDataPoint ) {
                            const firstValue = typeof get( firstDataPoint, guess.guessNo, undefined ) === "number"
                                ? get( firstDataPoint, guess.guessNo, 0 )
                                : 0;

                            changePercent = calc( "current - first | <=1", {
                                current: currentValue,
                                first: firstValue,
                            } ) as number;
                            isUp = changePercent >= 0;
                        }
                    }

                    return (
                        <div
                            key={guess.guessNo}
                            className={`flex items-center gap-2 min-w-0 ${isSingleGuess ? "flex-1" : ""}`}
                        >
                            {!isSingleGuess && (
                                <>
                                    <div
                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: CHART_COLORS[originalIndex % CHART_COLORS.length] }}
                                    />
                                    <span className="truncate text-xs font-normal leading-[15px] text-t3">
                                        {guess.title}
                                    </span>
                                    <span className="flex-shrink-0 text-xs font-medium leading-[15px] text-t1">
                                        {typeof get( selectedChartRow, guess.guessNo, undefined ) === "number"
                                            ? `${calc( "val | <=1", {
                                                val: get( selectedChartRow, guess.guessNo, 0 ),
                                            } ) as string}%`
                                            : guess.yesRealtimePrice
                                            ? formatCentPriceAsChancePercent( get( guess, "yesRealtimePrice" ) )
                                            : "-"}
                                    </span>
                                </>
                            )}
                            {isSingleGuess && (
                                <>
                                    {/* 百分比 + Chance */}
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <span className="text-xl font-[600] text-chart-2">
                                            {typeof get( selectedChartRow, guess.guessNo, undefined ) === "number"
                                                ? `${calc( "val | <=1", {
                                                    val: get( selectedChartRow, guess.guessNo, 0 ),
                                                } ) as string}%`
                                                : guess.yesRealtimePrice
                                                ? formatCentPriceAsChancePercent( get( guess, "yesRealtimePrice" ) )
                                                : "-"}
                                        </span>
                                        <span className="text-xl font-[600] text-chart-2">Chance</span>
                                    </div>

                                    {/* 涨跌幅 */}
                                    <div className="flex items-center gap-1 flex-shrink-0 font-[600]">
                                        <ChangeArrow isUp={isUp} />
                                        <span
                                            className={`text-xs font-[600] ${
                                                isUp ? "text-action-accent" : "text-state-danger"
                                            }`}
                                        >
                                            {calc( "change | <=1", { change: changePercent } ) as string}
                                            %
                                        </span>
                                    </div>

                                    {/* Logo */}
                                    <div className="ml-auto flex-shrink-0">
                                        <SmartImage
                                            src={getBrandLogoGray()}
                                            alt="Logo"
                                            width={112}
                                            height={24}
                                            className="h-6 w-[112px] object-contain"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    );
                } )}
            </div>
        </div>
    );
}
